/**
 * The fields every item has: name, how much, where, and a photo.
 *
 * Shared by the add sheet and the edit sheet so the two can't drift apart. It
 * holds no state — the sheet above owns the draft and decides what a change
 * means.
 */

import type { RefObject } from "react";

import { LOCATION_LABELS, STORAGE_LOCATIONS, UNITS } from "../features/food/types";
import type { FoodItemDraft, Unit } from "../features/food/types";
import type { DraftErrors } from "../features/food/validateDraft";
import { todayIso } from "../lib/date/isoDate";
import { Icon, LOCATION_ICONS } from "./Icon";
import { PhotoField } from "./PhotoField";

import "./ItemFields.css";

interface ItemFieldsProps {
  draft: FoodItemDraft;
  errors: DraftErrors;
  photo: string | null;
  /** True while a photo is being read; marks the fields about to change. */
  analyzing?: boolean;
  nameRef?: RefObject<HTMLInputElement>;
  onField: <K extends keyof FoodItemDraft>(field: K, value: FoodItemDraft[K]) => void;
  onPhoto: (photo: string | null) => void;
}

export function ItemFields({
  draft,
  errors,
  photo,
  analyzing = false,
  nameRef,
  onField,
  onPhoto,
}: ItemFieldsProps) {
  // Read once per render so the cap can't drift mid-interaction.
  const today = todayIso();

  return (
    <div className="fields" data-analyzing={analyzing}>
      <label className="field fields__pending">
        <span className="field__label">Name</span>
        <input
          ref={nameRef}
          className="field__input"
          value={draft.name}
          onChange={(event) => onField("name", event.target.value)}
          autoComplete="off"
          aria-invalid={Boolean(errors.name)}
        />
      </label>

      <div className="fields__amount">
        <label className="field">
          <span className="field__label">Amount</span>
          <input
            className="field__input"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={draft.quantity}
            onChange={(event) => onField("quantity", event.target.value)}
            aria-invalid={Boolean(errors.quantity)}
          />
        </label>

        <label className="field">
          <span className="field__label">Unit</span>
          <select
            className="field__input"
            value={draft.unit}
            onChange={(event) => onField("unit", event.target.value as Unit)}
          >
            {UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="field fields__pending">
        <legend className="field__label">Where</legend>
        <div className="fields__choices">
          {STORAGE_LOCATIONS.map((location) => (
            <button
              className="field__choice"
              key={location}
              type="button"
              aria-pressed={draft.location === location}
              onClick={() => onField("location", location)}
            >
              <Icon name={LOCATION_ICONS[location]} className="field__choice-icon" />
              {LOCATION_LABELS[location]}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span className="field__label">Bought</span>
        {/*
          The listed gap that turned out to be one missing input. `purchaseDate`
          has always been on the draft, always been validated (valid ISO, never
          in the future), and has always been what the expiry estimate counts
          from — there was simply nowhere to type it, so every item claimed to
          have been bought today.

          That matters most for a shop: half of it wasn't bought today, and a
          receipt knows exactly when it was. In the add sheet the best-before
          moves as this moves. In the edit sheet it deliberately doesn't — there
          the best-before is set by hand and this is a record of fact, so
          changing one must not silently rewrite the other.
        */}
        <input
          className="field__input"
          type="date"
          value={draft.purchaseDate}
          max={today}
          onChange={(event) => onField("purchaseDate", event.target.value)}
          aria-invalid={Boolean(errors.purchaseDate)}
        />
      </label>

      {errors.purchaseDate && (
        <p className="fields__error" role="alert">
          <Icon name="alert" className="fields__error-icon" />
          {errors.purchaseDate}
        </p>
      )}

      <PhotoField photo={photo} onChange={onPhoto} />
    </div>
  );
}
